import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  BehaviorSubject,
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { Project } from '../models/project/project.interface';
import { ProjectList } from '../models/project/project-list.interface';
import { Convertor } from '../utils/convertor';
import { ProjectForUpsert } from '../models/project/project.model';
import { Util } from '../utils/util';
import { RequestModel } from '../models/request-model.interface';
import { SearchProject } from '../models/search/search-project.interface';
import { OrganizationProject } from '../models/organization/organizaiton-project.interface';
import { CanHasImage } from '../interfaces/can-has-image.interface';
import { Constants } from '../constants';
import { FileService } from './file.service';

@Injectable()
export class ProjectService {
  private httpClient = inject(HttpClient);
  private readonly fileService = inject(FileService);

  private readonly projectsForSearchCache = new Map<string, BehaviorSubject<SearchProject[]>>();

  private readonly organizationProjectsCache = new Map<
    string,
    BehaviorSubject<OrganizationProject[]>
  >();

  private projectsUrl = '';

  public setProjectsUrl(companyId: string) {
    this.projectsUrl = `${environment.apiUrl}/companies/${companyId}/projects`;
  }

  private companyId = '';

  public setCompanyId(companyId: string) {
    this.companyId = companyId;
  }

  public getProjects(model: RequestModel): Observable<ProjectList[]> {
    return this.httpClient.post<ProjectList[]>(`${this.projectsUrl}/filtered-projects`, model).pipe(
      map(projects =>
        projects.map(project => {
          project.assignments = project.assignments.map(a => ({
            ...a,
            startDate: a.startDate ? new Date(a.startDate + 'Z') : undefined,
            deadline: a.deadline
              ? Util.getAssignmentDeadlineDate(a.deadline.toString())
              : undefined,
            timeLeft: Convertor.getTimeDifferenceString(
              new Date(),
              a.deadline ? new Date(a.deadline) : undefined,
            ),
          }));

          return project;
        }),
      ),
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public loadProjectsForSearch(
    companyId: string,
    excludedOrganizationId?: string,
  ): Observable<SearchProject[]> {
    let params = new HttpParams();

    if (excludedOrganizationId) {
      params = params.set('excludedOrganizationId', excludedOrganizationId);
    }

    if (!this.projectsForSearchCache.has(companyId)) {
      this.projectsForSearchCache.set(companyId, new BehaviorSubject<SearchProject[]>([]));
    }

    return this.httpClient
      .get<SearchProject[]>(`${this.projectsUrl}/projects-for-search`, { params })
      .pipe(
        switchMap(projects => this.enrichProjectsWithImages$(projects)),
        tap(projects => {
          this.projectsForSearchCache.get(companyId)!.next(projects);
        }),
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public getProjectsForSearch(companyId: string): Observable<SearchProject[]> {
    if (!this.projectsForSearchCache.has(companyId)) {
      this.projectsForSearchCache.set(companyId, new BehaviorSubject<SearchProject[]>([]));
    }

    return this.projectsForSearchCache.get(companyId)!.asObservable();
  }

  public loadOrganizationProjects(
    companyId: string,
    organizationId: string,
  ): Observable<OrganizationProject[]> {
    const cacheKey = `${companyId}::${organizationId}`;

    if (!this.organizationProjectsCache.has(cacheKey)) {
      this.organizationProjectsCache.set(cacheKey, new BehaviorSubject<OrganizationProject[]>([]));
    }

    return this.httpClient
      .get<OrganizationProject[]>(`${this.projectsUrl}/organization-projects/${organizationId}`)
      .pipe(
        switchMap(projects => this.enrichProjectsWithImages$(projects)),
        tap(projects => {
          this.organizationProjectsCache.get(cacheKey)!.next(projects);
        }),
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public getOrganizationProjects(
    companyId: string,
    organizationId: string,
  ): Observable<OrganizationProject[]> {
    const cacheKey = `${companyId}::${organizationId}`;

    if (!this.organizationProjectsCache.has(cacheKey)) {
      this.organizationProjectsCache.set(cacheKey, new BehaviorSubject<OrganizationProject[]>([]));
    }

    return this.organizationProjectsCache.get(cacheKey)!.asObservable();
  }

  public getProject(id: string): Observable<Project> {
    return this.httpClient.get<Project>(`${this.projectsUrl}/${id}`).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public createProject(project: ProjectForUpsert): Observable<string> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.httpClient.post<any>(this.projectsUrl, project).pipe(
      map(p => p.id),
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public deleteProject(id: string): Observable<null> {
    return this.httpClient.delete<null>(`${this.projectsUrl}/${id}`).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public updateProject(id: string, project: ProjectForUpsert): Observable<null> {
    return this.httpClient.put<null>(`${this.projectsUrl}/${id}`, project).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public archiveProject(id: string): Observable<void> {
    return this.httpClient.put<void>(`${this.projectsUrl}/${id}/archive`, null);
  }

  public unarchiveProject(id: string): Observable<void> {
    return this.httpClient.put<void>(`${this.projectsUrl}/${id}/unarchive`, null);
  }

  public patchProject(projectId: string, field: string, value: unknown): Observable<null> {
    const patchPayload = { [field]: value };

    return this.httpClient.patch<null>(`${this.projectsUrl}/${projectId}`, patchPayload, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
  }

  public hasOrganizationProjects(companyId: string, organizationId: string): boolean {
    const key = `${companyId}::${organizationId}`;
    
    return this.organizationProjectsCache.has(key) 
      && this.organizationProjectsCache.get(key)!.getValue().length > 0;
  }

  public hasProjectsForSearch(companyId: string): boolean {
    return this.projectsForSearchCache.has(companyId) 
      && this.projectsForSearchCache.get(companyId)!.getValue().length > 0;
  }

  public invalidateOrganizationProjects(companyId: string, organizationId: string): void {
    const key = `${companyId}::${organizationId}`;
    const subject = this.organizationProjectsCache.get(key);

    if (subject) {
      subject.next([]);
    }
  }

  public invalidateProjectsForSearch(companyId: string): void {
    const subject = this.projectsForSearchCache.get(companyId);

    if (subject) {
      subject.next([]);
    }
  }

  private getProjectWithImage$<T extends CanHasImage>(project: T): Observable<T> {
    if (!project.titleImageExtension) {
      project.imageUrl = Constants.NotFound;
      return of(project);
    }

    return this.fileService.getEntityWithImageUrl(
      project,
      `companies/${this.companyId}/projects/${project.id}/title${project.titleImageExtension}`,
    );
  }

  private enrichProjectsWithImages$<T extends CanHasImage>(projects: T[]): Observable<T[]> {
    if (!projects.length) {
      return of([]);
    }

    return forkJoin(projects.map(project => this.getProjectWithImage$(project)));
  }
}
